import React, { useRef, useState } from 'react';
import { useProdutos, useCriarProduto, useAtualizarProduto, useAlternarStatusProduto, useUploadFotoProduto } from '../../hooks/useProdutos';
import { useIngredientes } from '../../hooks/useIngredientes';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Produto } from '../../types';
import Button from '../../components/Common/Button';

interface ProdutoFormData {
  nome: string;
  descricao: string;
  precoUnitario: string;
  custo: string;
  tempoPreparoMinutos: string;
}

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');

const getImagemUrl = (urlFoto?: string | null): string | null => {
  if (!urlFoto) return null;
  return `${API_BASE_URL}${urlFoto}`;
};

const Produtos: React.FC = () => {
  usePageTitle('Produtos');
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const { data: produtos, isLoading } = useProdutos(!mostrarInativos);
  const { data: ingredientes, isLoading: carregandoIngredientes } = useIngredientes();
  const { mutate: criarProduto, isPending: criando } = useCriarProduto();
  const { mutate: atualizarProduto, isPending: atualizando } = useAtualizarProduto();
  const { mutate: alternarStatus } = useAlternarStatusProduto();
  const { mutate: uploadFoto, isPending: enviandoFoto } = useUploadFotoProduto();

  const [mostrarModal, setMostrarModal] = useState(false);
  const [produtoEditando, setProdutoEditando] = useState<Produto | null>(null);
  const [formData, setFormData] = useState<ProdutoFormData>({
    nome: '',
    descricao: '',
    precoUnitario: '',
    custo: '',
    tempoPreparoMinutos: '',
  });
  const [ingredientesQuantidades, setIngredientesQuantidades] = useState<{ [key: number]: number }>({});
  const [ingredientesDisplay, setIngredientesDisplay] = useState<{ [key: number]: string }>({});
  const [sobEncomenda, setSobEncomenda] = useState<boolean>(false);
  const [fotoSelecionada, setFotoSelecionada] = useState<File | null>(null);
  const [previewFoto, setPreviewFoto] = useState<string | null>(null);
  const inputFotoRef = useRef<HTMLInputElement>(null);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const abrirModalNovo = () => {
    setProdutoEditando(null);
    setFormData({ nome: '', descricao: '', precoUnitario: '', custo: '', tempoPreparoMinutos: '' });
    setIngredientesQuantidades({});
    setIngredientesDisplay({});
    setSobEncomenda(false);
    setFotoSelecionada(null);
    setPreviewFoto(null);
    setMostrarModal(true);
  };

  const abrirModalEditar = (produto: Produto) => {
    setProdutoEditando(produto);
    setFormData({
      nome: produto.nome,
      descricao: produto.descricao,
      precoUnitario: String(produto.precoUnitario),
      custo: String(produto.custo),
      tempoPreparoMinutos: String(produto.tempoPreparoMinutos),
    });
    const quantidades: { [key: number]: number } = {};
    const display: { [key: number]: string } = {};
    if (produto.ingredientes && produto.ingredientes.length > 0) {
      produto.ingredientes.forEach(ing => {
        quantidades[ing.ingredienteId] = ing.quantidade;
        display[ing.ingredienteId] = String(ing.quantidade);
      });
    }
    setIngredientesQuantidades(quantidades);
    setIngredientesDisplay(display);
    setSobEncomenda(produto.sobEncomenda);
    setFotoSelecionada(null);
    setPreviewFoto(getImagemUrl(produto.urlFoto));
    setMostrarModal(true);
  };

  const fecharModal = () => {
    setMostrarModal(false);
    setProdutoEditando(null);
    setFormData({ nome: '', descricao: '', precoUnitario: '', custo: '', tempoPreparoMinutos: '' });
    setIngredientesQuantidades({});
    setIngredientesDisplay({});
    setSobEncomenda(false);
    setFotoSelecionada(null);
    setPreviewFoto(null);
  };

  const handleFotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFotoSelecionada(file);
    setPreviewFoto(URL.createObjectURL(file));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const ingredientes = Object.entries(ingredientesQuantidades)
      .filter(([_, quantidade]) => quantidade > 0)
      .map(([ingredienteId, quantidade]) => ({ ingredienteId: parseInt(ingredienteId), quantidade }));

    const precoUnitario = parseFloat(formData.precoUnitario.replace(',', '.')) || 0;
    const custo = parseFloat(formData.custo.replace(',', '.')) || 0;
    const tempoPreparoMinutos = parseInt(formData.tempoPreparoMinutos) || 0;

    if (produtoEditando) {
      atualizarProduto(
        { ...produtoEditando, nome: formData.nome, descricao: formData.descricao, precoUnitario, custo, tempoPreparoMinutos, sobEncomenda, ingredientes },
        {
          onSuccess: () => {
            if (fotoSelecionada) {
              uploadFoto({ id: produtoEditando.id, foto: fotoSelecionada }, { onSettled: () => fecharModal() });
            } else {
              fecharModal();
            }
          },
        }
      );
    } else {
      criarProduto(
        { nome: formData.nome, descricao: formData.descricao, precoUnitario, custo, tempoPreparoMinutos, sobEncomenda, ingredientes },
        {
          onSuccess: (data: any) => {
            if (fotoSelecionada && data?.id) {
              uploadFoto({ id: data.id, foto: fotoSelecionada }, { onSettled: () => fecharModal() });
            } else {
              fecharModal();
            }
          },
        }
      );
    }
  };

  const handleQuantidadeChange = (ingredienteId: number, quantidade: number) => {
    setIngredientesQuantidades(prev => {
      if (quantidade <= 0) {
        const novo = { ...prev };
        delete novo[ingredienteId];
        return novo;
      }
      return { ...prev, [ingredienteId]: quantidade };
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Produtos</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={mostrarInativos}
              onChange={(e) => setMostrarInativos(e.target.checked)}
              className="rounded"
            />
            <span className="text-gray-700">Mostrar inativos</span>
          </label>
          <Button onClick={abrirModalNovo} variant="primary" size="md">
            Novo Produto
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">Carregando produtos...</p>
        </div>
      ) : produtos && produtos.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Foto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preço
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Custo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tempo Preparo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {produtos.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')).map((produto) => (
                <tr key={produto.id} className={!produto.ativo ? 'bg-gray-50' : ''}>
                  <td className="px-4 py-3">
                    {produto.urlFoto ? (
                      <img
                        src={getImagemUrl(produto.urlFoto)!}
                        alt={produto.nome}
                        className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 text-xs text-center">
                        Sem foto
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{produto.nome}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">{produto.descricao}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">
                      {formatCurrency(produto.precoUnitario)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{formatCurrency(produto.custo)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{produto.tempoPreparoMinutos} min</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        produto.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {produto.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => abrirModalEditar(produto)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => alternarStatus(produto.id)}
                      className={produto.ativo ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                    >
                      {produto.ativo ? 'Desativar' : 'Ativar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">Nenhum produto encontrado</p>
        </div>
      )}

      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {produtoEditando ? 'Editar Produto' : 'Novo Produto'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Foto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Foto do Produto</label>
                <div className="flex items-center gap-3">
                  <div
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400 transition-colors flex-shrink-0"
                    onClick={() => inputFotoRef.current?.click()}
                  >
                    {previewFoto ? (
                      <img src={previewFoto} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-gray-400 text-xs text-center px-1">Clique para adicionar</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      ref={inputFotoRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleFotoChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => inputFotoRef.current?.click()}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      {previewFoto ? 'Trocar foto' : 'Selecionar foto'}
                    </button>
                    {fotoSelecionada && (
                      <p className="text-xs text-gray-500 mt-1 truncate">{fotoSelecionada.name}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">JPG, PNG, GIF ou WebP · máx. 10MB</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preço Unitário (R$)</label>
                <input
                  type="text"
                  value={formData.precoUnitario}
                  onChange={(e) => setFormData({ ...formData, precoUnitario: e.target.value.replace(/[^0-9.,]/g, '') })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custo (R$)</label>
                <input
                  type="text"
                  value={formData.custo}
                  onChange={(e) => setFormData({ ...formData, custo: e.target.value.replace(/[^0-9.,]/g, '') })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tempo de Preparo (minutos)</label>
                <input
                  type="text"
                  value={formData.tempoPreparoMinutos}
                  onChange={(e) => setFormData({ ...formData, tempoPreparoMinutos: e.target.value.replace(/[^0-9]/g, '') })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                  required
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sobEncomenda"
                  checked={sobEncomenda}
                  onChange={(e) => setSobEncomenda(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="sobEncomenda" className="text-sm font-medium text-gray-700">
                  Sob Encomenda (não aparecerá no PDV)
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ingredientes (quantidade necessária por unidade)
                </label>
                {carregandoIngredientes ? (
                  <p className="text-sm text-gray-500">Carregando ingredientes...</p>
                ) : ingredientes && ingredientes.length > 0 ? (
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-3 space-y-3">
                    {ingredientes.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')).map((ingrediente) => (
                      <div key={ingrediente.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded">
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-700">{ingrediente.nome}</span>
                          <span className="text-xs text-gray-500 ml-2">
                            (Estoque: {ingrediente.quantidadeEstoque} {ingrediente.unidadeMedidaSigla})
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            placeholder="0.000"
                            value={ingredientesDisplay[ingrediente.id] ?? ''}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/[^0-9.,]/g, '');
                              setIngredientesDisplay(prev => ({ ...prev, [ingrediente.id]: raw }));
                              handleQuantidadeChange(ingrediente.id, parseFloat(raw.replace(',', '.')) || 0);
                            }}
                            className="w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-600">{ingrediente.unidadeMedidaSigla}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Nenhum ingrediente cadastrado</p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  fullWidth
                  disabled={criando || atualizando || enviandoFoto}
                >
                  {criando || atualizando || enviandoFoto ? 'Salvando...' : produtoEditando ? 'Atualizar' : 'Criar'}
                </Button>
                <Button
                  type="button"
                  onClick={fecharModal}
                  variant="secondary"
                  size="md"
                  fullWidth
                  disabled={criando || atualizando || enviandoFoto}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Produtos;
